import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../../../Components/Header/Header";
import Icons from "../../../Constants/Icons";
import Colors from "../../../Constants/Colors";
import CustomUserScreen from "../../../Components/CustomUserScreen/CustomUserScreen";
import CustomArtistVerification from "../../../Components/CustomArtistVerification/CustomArtistVerification";
import CustomDropdown from "../../../Components/DropDown/CustomDropdown";
import CustomPagination from "../../../Components/CustomPagination/CustomPagination";
import {
  apiCallGetUser,
  apiCallToggleUserStatus,
  onAPiCallAdminToggleFeaturedUser,
  onAPiCallUserSearchUser,
} from "../../../Core/Apicall";
import {
  setLoadingFalse,
  setLoadingTrue,
} from "../../../Config/Actions/loadingActions";
import EmptyData from "../../../Components/EmptyData/EmptyData";
import UseDebounce from "../../../Components/UseDebounce";
import { CustomCommonBox } from "../../../Components/CustomCommonBox/CustomCommonBox";

const CHIPS = [
  { label: "ARTLOVER", userType: "ArtLover", route: "/ArtLover" },
  { label: "ARTIST", userType: "Artist", route: "/Artist" },
  { label: "ARTIST PRO", userType: "ArtistPro", route: "/ArtistPro" },
  { label: "ARTRUNNER", userType: "ArtRunner", route: "/ArtRunner" },
];

const ITEMS_PER_PAGE = 15;

function ChipBar({ selected, onChange }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        padding: "5px",
        background: "rgba(6, 101, 228, 0.15)",
        border: "1px solid rgba(6, 101, 228, 0.35)",
        boxShadow: "0 4px 24px rgba(6, 101, 228, 0.15)",
        borderRadius: "999px",
        backdropFilter: "blur(8px)",
      }}
    >
      {CHIPS.map((c) => {
        const active = selected === c.label;
        return (
          <button
            key={c.label}
            onClick={() => onChange(c)}
            style={{
              height: "38px",
              padding: "0 22px",
              borderRadius: "999px",
              border: "none",
              background: active ? "rgba(6, 101, 228, 0.75)" : "transparent",
              color: active ? "#FFFFFF" : "rgba(255,255,255,0.55)",
              boxShadow: active ? "0 2px 12px rgba(6, 101, 228, 0.4)" : "none",
              fontSize: "12px",
              fontWeight: active ? "700" : "500",
              letterSpacing: "0.8px",
              textTransform: "uppercase",
              cursor: "pointer",
              transition: "all 0.2s ease",
              whiteSpace: "nowrap",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}


function SearchBox({ value, onChange, placeholder, disabled, inputRef }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
        padding: "0 18px",
        height: "48px",
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: "999px",
        backdropFilter: "blur(8px)",
        minWidth: "260px",
      }}
    >
      <img
        src={Icons.ic_search}
        alt="search"
        style={{
          width: "18px",
          height: "18px",
          opacity: 0.5,
          filter: "brightness(0) invert(1)",
          flexShrink: 0,
        }}
      />
      <input
        ref={inputRef}
        type="text"
        disabled={disabled}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          background: "transparent",
          border: "none",
          outline: "none",
          color: "#FFFFFF",
          fontSize: "13px",
          fontWeight: "400",
          fontFamily: "Poppins, sans-serif",
          letterSpacing: "0.3px",
          width: "100%",
        }}
      />
    </div>
  );
}


function UserSection() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoading = useSelector((state) => state.isLoading);
  const getChipFromPath = (pathname) => {
    if (pathname === "/ArtLover") return CHIPS[0];
    return CHIPS.find((c) => c.route === pathname) || CHIPS[0];
  };
  const [selectedChip, setSelectedChip] = useState(() => getChipFromPath(location.pathname));
  const [userListData, setUserListData] = useState();
  const [userListAllData, setUserListAllData] = useState();
  const [userTotalActive, setUserTotalActive] = useState(0);
  const [userTotalInActive, setUserTotalInActive] = useState(0);
  const [userTotalCount, setUserTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [dropdownLabel, setDropdownLabel] = useState("By Status");
  const [initialPage, setInitialPage] = useState(0);
  const inputRef = useRef(null);
  const debouncedSearch = UseDebounce(searchValue, 500);
  const isInitialRender = useRef(true);

  useEffect(() => {
    const matched = getChipFromPath(location.pathname);
    if (matched.label !== selectedChip.label) setSelectedChip(matched);
  }, [location.pathname]);

  const handleChipChange = (chip) => {
    setSelectedChip(chip);
    setSearchValue("");
    setInitialPage(0);
    setDropdownLabel("By Status");
    navigate(chip.route);
  };

  const fetchUsers = useCallback(
    async (page, status, userType) => {
      dispatch(setLoadingTrue());
      const param = { offset: page + 1, status, limit: ITEMS_PER_PAGE, userType: userType || undefined };
      try {
        const res = await apiCallGetUser(param);
        setUserTotalActive(res?.allActiveUser);
        setUserTotalCount(res?.allUser);
        setUserTotalInActive(res?.allDeActiveUser);
        setInitialPage(page);
        setUserListData(res);
        setUserListAllData(res);
      } catch (e) { console.error(e); }
      finally { dispatch(setLoadingFalse()); }
    }, [dispatch]
  );

  const searchUsers = useCallback(
    async (text, page, status, userType) => {
      dispatch(setLoadingTrue());
      const param = { searchQuery: text, status, offset: page + 1, limit: ITEMS_PER_PAGE, userType: userType || undefined };
      try {
        const res = await onAPiCallUserSearchUser(param);
        setInitialPage(page);
        setUserListData(res);
        setUserListAllData(res);
      } catch (e) { console.error(e); }
      finally {
        dispatch(setLoadingFalse());
        setTimeout(() => inputRef.current?.focus(), 600);
      }
    }, [dispatch]
  );

  useEffect(() => {
    isInitialRender.current = true; fetchUsers(0, dropdownLabel, selectedChip.userType);
  }, [selectedChip]);

  useEffect(() => {
    if (isInitialRender.current) { isInitialRender.current = false; return; }
    searchUsers(searchValue, 0, dropdownLabel, selectedChip.userType);
  }, [debouncedSearch]);


  const onClickStatusChange = async (data) => {
    dispatch(setLoadingTrue());
    try {
      await apiCallToggleUserStatus({ userId: data.userId, isEnabled: !data.isEnabled });
      if (data.isEnabled) { setUserTotalActive((p) => p - 1); setUserTotalInActive((p) => p + 1); }
      else { setUserTotalActive((p) => p + 1); setUserTotalInActive((p) => p - 1); }
      const updater = (prev) => ({ ...prev, data: prev.data.map((item) => item.userId === data.userId ? { ...item, isEnabled: !item.isEnabled } : item) });
      setUserListData(updater); setUserListAllData(updater);
    } catch (e) { console.error(e); }
    finally { dispatch(setLoadingFalse()); }
  };

  const onClickFeatureStatusChange = async (data) => {
    dispatch(setLoadingTrue());
    try {
      await onAPiCallAdminToggleFeaturedUser({ userId: data.userId, isFeatured: !data.isFeatured });
      const updater = (prev) => ({ ...prev, data: prev.data.map((item) => item.userId === data.userId ? { ...item, isFeatured: !item.isFeatured } : item) });
      setUserListData(updater); setUserListAllData(updater);
    } catch (e) { console.error(e); }
    finally { dispatch(setLoadingFalse()); }
  };

  const onClickLogoVerified = async (data) => {
    dispatch(setLoadingTrue());
    try {
      await onAPiCallAdminToggleFeaturedUser({ userId: data.userId, isLogoVerifiedStatus: true, isLogoVerified: !data.isLogoVerified });
      const updater = (prev) => ({ ...prev, data: prev.data.map((item) => item.userId === data.userId ? { ...item, isLogoVerified: !item.isLogoVerified } : item) });
      setUserListData(updater); setUserListAllData(updater);
    } catch (e) { console.error(e); }
    finally { dispatch(setLoadingFalse()); }
  };

  const isArtist = selectedChip.label === "ARTIST" || selectedChip.label === "ARTIST PRO";
  const statLabels = {
    "ARTLOVER": "Total ArtLover",
    "ARTIST": "Total Artist",
    "ARTIST PRO": "Total Artist Pro",
    "ARTRUNNER": "Total ArtRunner"
  };

  const statLabel = statLabels[selectedChip.label] || "Total ArtRunner";
  const UserStats = [
    { label: statLabel, count: userTotalCount },
    { label: "Total Activate", color: Colors.darkGreen, count: userTotalActive },
    { label: "Total Deactivate", color: Colors.darkBlue, count: userTotalInActive },
  ];

  const renderTableHeader = () => {
    if (isArtist) {
      return (
        <nav className="grid-cols grid_7 fontSize16WithoutFlex transparentWhite-padding">
          <div style={{ width: "20%" }} className="user_name">Name / Copy ID</div>
          <div style={{ width: "14%" }} className="user_name">UserName</div>
          <div style={{ width: "20%" }} className="user_name">Contact</div>
          <div style={{ width: "9%" }} className="user_name">Date of birth</div>
          <div style={{ width: "5%" }} className="user_name">User type</div>
          <div style={{ width: "4%" }} className="user_name w-10">Edit</div>
          <div style={{ width: "9%" }} className="user_name">Status</div>
          <div style={{ width: "9%" }} className="user_name">Featured</div>
          <div style={{ width: "10%" }} className="user_name">Verified</div>
        </nav>
      );
    }

    return (
      <nav className="grid-cols grid_7 fontSize16WithoutFlex transparentWhite-padding">
        <div style={{ width: "22%" }} className="user_name">Name / Copy ID</div>
        <div style={{ width: "16%" }} className="user_name">UserName</div>
        <div style={{ width: "22%" }} className="user_name">Contact</div>
        <div style={{ width: "9%" }} className="user_name">Date of birth</div>
        <div style={{ width: "7%" }} className="user_name">User type</div>
        <div style={{ width: "4%" }} className="user_name w-10">Edit</div>
        <div style={{ width: "10%" }} className="user_name">Status</div>
        <div style={{ width: "10%" }} className="user_name">Featured</div>
      </nav>
    );
  };

  const renderTableBody = () => {
    if (isArtist) {
      return (
        <CustomArtistVerification
          currentUsers={userListData?.data}
          onClickStatusChange={onClickStatusChange}
          onClickFeatureStatusChange={onClickFeatureStatusChange}
          onClickLogoVerified={onClickLogoVerified}
        />
      );
    }
    return (
      <CustomUserScreen
        currentUsers={userListData?.data}
        onClickStatusChange={onClickStatusChange}
        onClickFeatureStatusChange={onClickFeatureStatusChange}
      />
    );
  };

  const statLabelsList = {
    "ARTLOVER": "ArtLover List",
    "ARTIST": "Artist List",
    "ARTIST PRO": "Artist Pro List",
    "ARTRUNNER": "ArtRunner List"
  };

  const statLabelListSet = statLabels[statLabelsList.label] || "ArtRunner List";

  const listTitle = statLabelListSet;
  const dataLength = userListData?.data?.length;
  const totalCount = userListData?.totalCount;

  return (
    <div>
      <Header name={"Users"} />
      <div className="register-flex">
        <div className="astrologer-inner">
          <div className="frame-container">
            <div className="frame-div">
              {UserStats.map((tab, i) => (
                <CustomCommonBox key={i} label={tab.label} color={tab.color} count={tab.count} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="astrologers-list-parent padding-top-bottom">
        <div className="frame-parent2">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <ChipBar selected={selectedChip.label} onChange={handleChipChange} />

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div className="custom-container-dropdown-div">
                <CustomDropdown
                  dropDownLabel={dropdownLabel}
                  items={["All", "Activate", "Deactivate"]}
                  color="blue"
                  onSelect={(f) => {
                    setDropdownLabel(f)
                    if (searchValue == "") {
                      fetchUsers(0, f, selectedChip.userType)
                    } else {
                      searchUsers(searchValue, 0, f, selectedChip.userType);
                    }
                  }}
                  customLabelStyle={{ fontWeight: "400", color: "white" }}
                />
              </div>

              <div className="custom-box-search-div" style={{ marginLeft: "auto" }}>
                <img className="custom-image" alt="" src={Icons.ic_search} />
                <input
                  type="text"
                  disabled={isLoading}
                  ref={inputRef}
                  className="searchTextInput"
                  placeholder={"Search..."}
                  value={searchValue}
                  onChange={(e) =>
                    setSearchValue(e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="Event-custom-style">
        <div className="lastWeekEndDiv Event-AlignSelf-stretch">
          <div className="user_title">{listTitle}</div>
          <div className="transparentWhite">
            {renderTableHeader()}
            {renderTableBody()}
          </div>
        </div>
      </div>

      {dataLength === 0 && (
        <div className="empty-centered"><EmptyData /></div>
      )}

      {dataLength !== 0 && (
        <div className="Custom-Pagination">
          <CustomPagination
            pageCount={Math.ceil(totalCount / ITEMS_PER_PAGE)}
            pageRangeDisplayed={3}
            initialPage={initialPage}
            marginPagesDisplayed={1}
            onPageChange={({ selected }) => {
              if (searchValue == "") {
                fetchUsers(selected, dropdownLabel, selectedChip.userType)
              } else {
                searchUsers(searchValue, selected, dropdownLabel, selectedChip.userType);
              }
            }}
          />
        </div>
      )}
    </div>
  );
}

export default UserSection;